import { MapPin, Clock, Award, Heart, Sparkles } from 'lucide-react';

const OurStory = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7] pt-32 pb-20 overflow-x-hidden font-sans">
      
      {/* --- BACKGROUND ACCENTS --- */}
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none z-0">
          <div className="absolute top-[10%] left-[-10%] w-[400px] h-[400px] bg-pink-100 rounded-full blur-[80px] opacity-60"></div>
          <div className="absolute bottom-[20%] right-[-5%] w-[300px] h-[300px] bg-yellow-100 rounded-full blur-[80px] opacity-60"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        
        {/* --- 1. HERO HEADER --- */}
        <div className="text-center max-w-3xl mx-auto mb-20 space-y-6">
            <span className="inline-block px-4 py-1.5 rounded-full border border-gray-900 text-gray-900 text-xs font-bold tracking-[0.2em] uppercase">
                Est. 2021
            </span>
            <h1 className="text-5xl md:text-7xl font-black text-gray-900 leading-tight">
                Crafting Joy, <br/>
                <span className="italic font-serif font-light text-gray-500">One Scoop at a Time.</span>
            </h1>
            <p className="text-gray-600 text-lg md:text-xl leading-relaxed">
                It started with a simple idea in the heart of Dhaka: Ice cream shouldn't just be a dessert. It should be a memory.
            </p>
        </div>

        {/* --- 2. THE ORIGIN (Image + Text) --- */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-24">
            <div className="relative group">
                <div className="absolute inset-0 bg-black translate-x-4 translate-y-4 rounded-3xl transition-transform group-hover:translate-x-2 group-hover:translate-y-2"></div>
                <img 
                    src="https://images.unsplash.com/photo-1516559828984-fb3b99548b21?q=80&w=1000&auto=format&fit=crop" 
                    alt="Making Ice Cream" 
                    className="relative w-full h-[500px] object-cover rounded-3xl border-4 border-white shadow-xl grayscale group-hover:grayscale-0 transition-all duration-700"
                />
            </div>
            
            <div className="space-y-8">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                    <Sparkles className="text-yellow-500 fill-current" />
                    The Beginning
                </h2>
                <div className="space-y-6 text-gray-600 text-lg leading-relaxed border-l-2 border-pink-200 pl-6">
                    <p>
                        In 2021, amidst the chaos of the world, we found solace in sweetness. 
                        <strong className="text-gray-900"> Jilapi </strong> 
                        was born not in a boardroom, but in a small kitchen where passion met precision.
                    </p>
                    <p>
                        We didn't want to make "just another ice cream." We wanted to capture the vibrant energy of Dhaka and blend it with artisanal European techniques.
                    </p>
                    <p>
                        From sourcing the freshest milk to hand-picking seasonal fruits from local markets, every batch tells a story of dedication.
                    </p>
                </div>
            </div>
        </div>

        {/* --- 3. STATS (Premium Grid) --- */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-24">
            {[
                { label: "Founded", value: "2021", icon: <Clock /> },
                { label: "Flavors", value: "24+", icon: <Award /> },
                { label: "Happy Guests", value: "15k+", icon: <Heart /> },
                { label: "Quality", value: "100%", icon: <Sparkles /> },
            ].map((stat, idx) => (
                <div key={idx} className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 mx-auto bg-gray-50 rounded-full flex items-center justify-center text-gray-900 mb-4">
                        {stat.icon}
                    </div>
                    <h3 className="text-3xl font-black text-gray-900 mb-1">{stat.value}</h3>
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">{stat.label}</p>
                </div>
            ))}
        </div>

        {/* --- 4. THE LOCATION (Dark Mode Card) --- */}
        <div className="relative rounded-[3rem] overflow-hidden bg-[#1a1a1a] text-white p-8 md:p-16 text-center md:text-left">
            {/* Background Texture */}
            <div className="absolute top-0 right-0 w-full h-full opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>

            <div className="relative z-10 flex flex-col md:flex-row items-center gap-12">
                <div className="flex-1 space-y-8">
                    <div>
                        <span className="text-pink-500 font-bold tracking-widest uppercase text-sm">Visit Our Flagship</span>
                        <h2 className="text-4xl md:text-5xl font-black mt-2">The Banani Hub</h2>
                    </div>
                    
                    <p className="text-gray-400 text-lg max-w-md">
                        Come for the ice cream, stay for the vibe. Our flagship store is designed to be your sanctuary in the city.
                    </p>

                    <div className="space-y-4">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-gray-800 rounded-2xl text-pink-500">
                                <MapPin size={24} />
                            </div>
                            <div>
                                <h4 className="font-bold text-xl">Banani 12</h4>
                                <p className="text-gray-400">House 12, Road 12, Block E<br/>Dhaka 1212, Bangladesh</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-gray-800 rounded-2xl text-yellow-500">
                                <Clock size={24} />
                            </div>
                            <div>
                                <h4 className="font-bold text-xl">Opening Hours</h4>
                                <p className="text-gray-400">Daily: 11:00 AM - 11:00 PM</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Map/Image Placeholder */}
                <div className="flex-1 w-full h-[400px] rounded-[2.5rem] overflow-hidden border-8 border-gray-800 relative group">
                     {/* Replace with actual map or shop interior image */}
                    <img 
                        src="https://images.unsplash.com/photo-1554118811-1e0d58224f24?q=80&w=1000&auto=format&fit=crop" 
                        alt="Jilapi Banani Store" 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-8">
                        <p className="text-white/80 italic font-serif">"A sweet escape in Dhaka"</p>
                    </div>
                </div>
            </div>
        </div>

      </div>
    </div>
  );
};

export default OurStory;